import {
  Box,
  Button,
  Grid,
  Typography,
  AppBar,
  Toolbar,
  useTheme,
  TextField,
  Autocomplete,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import ptBR from "date-fns/locale/pt-BR";
import { format } from "date-fns";
import { useState } from "react";
import {
  darkenColor,
  dataGridTexts,
  formatCnpj,
  nameAbbreviation,
} from "../../../../../utils";
import { xlsxGenerator } from "../../../../../utils/convert-to-xlsx/xslxGenerator";
import { useAppSelector } from "../../../../../application/states/hooks";
import { useChatService } from "../../../../../use-cases/chat";

const ratingLabel = (r: number) => {
  if (r <= 2) return "Ruim";
  if (r === 3) return "Regular";
  return "Bom";
};

export const BackofficeChatSatisfactionPage = () => {
  const theme = useTheme();

  // Company State (Logged User Context)
  const { company: currentUserCompany } = useAppSelector((state) => state.company);

  // Backoffice State (for Company List)
  const { companyList } = useAppSelector((state) => state.backofficeCompany);

  const { getChatSatisfactionReport } = useChatService();

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [evaluations, setEvaluations] = useState<
    Array<{
      id: string;
      companyId: string;
      companyName: string;
      userId: string;
      userName: string;
      rating: number;
      comment?: string;
      createdAt: string;
    }>
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // State for local company selection (Client)
  const [localSelectedCompany, setLocalSelectedCompany] = useState<{
    id: string;
    razaoSocial: string;
    nomeFantasia?: string;
    cnpj: string;
    resaleId?: string;
  } | null>(null);

  // Verification
  const isResale = currentUserCompany?.resale === true;
  const isMaster = currentUserCompany?.master === true;
  const showClientSelector = isResale || isMaster;

  // Company Selection Logic
  const companyId = localSelectedCompany?.id ?? (isResale ? currentUserCompany?.id : (isMaster ? "" : currentUserCompany?.id)) ?? "";



  const handleSearch = async () => {
    // Company is now optional - if not selected, backend will return all accessible companies
    if (!startDate || !endDate) {
      setError("Selecione o período completo (data inicial e final).");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const result = await getChatSatisfactionReport({
        companyId: companyId || undefined, // Send undefined if empty to get all companies
        startDate: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
        endDate: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
      });
      setEvaluations(result.evaluations ?? []);
    } catch (e: any) {
      const msg =
        e?.response?.data?.result?.message ||
        e?.message ||
        "Erro ao carregar relatório.";
      setError(msg);
      setEvaluations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setEvaluations([]);
    setLocalSelectedCompany(null);
    setError(null);
  };

  const { resaleConfig } = useAppSelector((state) => state.resaleConfig);

  const handleExport = () => {
    if (evaluations.length === 0) return;

    const exportData = evaluations.map((row) => [
      format(new Date(row.createdAt), "dd/MM/yyyy HH:mm", { locale: ptBR }),
      row.companyName,
      row.userName,
      row.rating,
      ratingLabel(row.rating),
      row.comment || "",
    ]);

    const headers = ["Data", "Empresa", "Usuário", "Nota", "Classificação", "Comentário"];

    let primaryColor = "723989";
    let contrastColor = "FFFFFFFF";
    let fgColor = "e8e4d6";

    // Attempt to get colors from resale config if available
    if (resaleConfig) {
      if (resaleConfig.id !== "") {
        if (resaleConfig.theme?.colors?.primary?.main) {
          // xlsxGenerator expects hex without # for fgColor?
          // The generator implementation does .replace('#', '') internally for primary/contrast
          // But for fgColor it uses it directly in one place and not others? 
          // Looking at xlsxGenerator.ts: 
          // primaryColor.replace('#', '')
          // contrastColor.replace('#', '')
          // fgColor is used directly: fgColor: { argb: fgColor } which usually implies AARRGGBB or just RRGGBB without # if passed as argb?
          // However in BackofficeConsumptionPage it passes "e8e4d6" (no hash).
          // So we should pass hex without hash?
          // Actually BackofficeConsumptionPage passes:
          // primaryColor = resaleConfig.theme.colors.primary.main (usually has #)
          // contrastColor = ...
          // fgColor = "e8e4d6"
          // xlsxGenerator handles the removal of # for primary/contrast.

          primaryColor = resaleConfig.theme.colors.primary.main;
        }
        if (resaleConfig.theme?.colors?.primary?.contrastColor) {
          contrastColor = resaleConfig.theme.colors.primary.contrastColor;
        }
      }
    }

    xlsxGenerator(
      exportData,
      headers,
      "Relatório de Satisfação Chat",
      primaryColor,
      contrastColor,
      fgColor
    );
  };

  // Prepare options for Autocomplete
  const companyOptions = companyList.map(item => ({
    id: item.company.id,
    razaoSocial: item.company.razaoSocial,
    nomeFantasia: item.company.nomeFantasia,
    cnpj: item.company.cnpj,
    resaleId: item.company.resaleId
  })).sort((a, b) => a.razaoSocial.localeCompare(b.razaoSocial));

  // If Resale or Master, ensure their own company is also an option
  if ((isResale || isMaster) && currentUserCompany) {
    const alreadyExists = companyOptions.find(c => c.id === currentUserCompany.id);
    if (!alreadyExists) {
      companyOptions.unshift({
        id: currentUserCompany.id,
        razaoSocial: currentUserCompany.razaoSocial,
        nomeFantasia: currentUserCompany.nomeFantasia,
        cnpj: currentUserCompany.cnpj,
        resaleId: currentUserCompany.resaleId
      });
    }
  }

  // Add "All companies" option for Resale/Master
  if (isResale || isMaster) {
    companyOptions.unshift({
      id: "",
      razaoSocial: "Todas as empresas",
      nomeFantasia: undefined,
      cnpj: "",
      resaleId: undefined
    });
  }

  // DataGrid Columns
  const columns: GridColDef[] = [
    {
      field: "createdAt",
      headerName: "Data",
      headerAlign: "center",
      align: "center",
      minWidth: 160,
      renderCell: (params) => format(new Date(params.value), "dd/MM/yyyy HH:mm", { locale: ptBR }),
    },
    {
      field: "companyName",
      headerName: "Empresa",
      minWidth: 200,
      flex: 0.5,
    },
    {
      field: "userName",
      headerName: "Usuário",
      minWidth: 150,
      flex: 0.3,
    },
    {
      field: "rating",
      headerName: "Nota",
      headerAlign: "center",
      align: "center",
      width: 120,
      renderCell: (params) => `${params.value} — ${ratingLabel(params.value)}`,
    },
    {
      field: "comment",
      headerName: "O que achou (comentário)",
      flex: 1,
      minWidth: 300,
      renderCell: (params) => params.value || "—",
    },
  ];

  return (
    <Box>
      <AppBar sx={{ position: "relative" }}>
        <Toolbar>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            Relatório de satisfação do chat
          </Typography>
          <Button color="inherit" onClick={handleClearFilters}>
            Limpar filtros
          </Button>
        </Toolbar>
      </AppBar>

      <Box p={3}>
        <Grid container justifyContent="center" spacing={3}>
          {showClientSelector ? (
            <Grid item xs={12} md={6} lg={4}>
              <Autocomplete
                options={companyOptions}
                getOptionLabel={(option) => {
                  if (!option) return "";
                  return `${nameAbbreviation(option.razaoSocial)}${option.nomeFantasia
                    ? ` / ${nameAbbreviation(option.nomeFantasia)}`
                    : ""
                    } - ${formatCnpj(option.cnpj)}`;
                }}
                value={localSelectedCompany}
                onChange={(_, newValue) => setLocalSelectedCompany(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Selecione a empresa"
                    variant="outlined"
                    fullWidth
                  />
                )}
                noOptionsText="Nenhuma empresa encontrada"
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </Grid>
          ) : (
            <Grid item xs={12}>
              {isResale && (
                <Typography variant="body2" color="text.secondary" align="center">
                  Buscando dados da sua revenda (Padrão)
                </Typography>
              )}
            </Grid>
          )}

          <Grid item>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
              <DatePicker
                label="Data inicial"
                value={startDate}
                onChange={(v) => setStartDate(v)}
                slotProps={{
                  day: {
                    sx: {
                      "&.MuiPickersDay-root.Mui-selected": {
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        "&:hover": {
                          backgroundColor: darkenColor(
                            theme.palette.primary.main,
                            10
                          ),
                        },
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
              <DatePicker
                label="Data final"
                value={endDate}
                onChange={(v) => setEndDate(v)}
                slotProps={{
                  day: {
                    sx: {
                      "&.MuiPickersDay-root.Mui-selected": {
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        "&:hover": {
                          backgroundColor: darkenColor(
                            theme.palette.primary.main,
                            10
                          ),
                        },
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSearch}
              disabled={loading || !startDate || !endDate}
              sx={{
                "&:hover": {
                  backgroundColor: darkenColor(theme.palette.primary.main, 10),
                },
                color: theme.palette.primary.contrastText,
              }}
            >
              {loading ? "Carregando…" : "Gerar relatório"}
            </Button>
          </Grid>

          {error && (
            <Grid item xs={12}>
              <Typography color="error" align="center">
                {error}
              </Typography>
            </Grid>
          )}

          {evaluations.length > 0 && (
            <Grid item xs={12} container spacing={2} justifyContent="center">
              <Grid item>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleExport}
                >
                  Exportar XLSX
                </Button>
              </Grid>
            </Grid>
          )}

          <Grid item xs={12}>
            {/* Tabela de Resultados */}
            {evaluations.length > 0 && (
              <div style={{ height: 600, width: '100%' }}>
                <DataGrid
                  rows={evaluations}
                  columns={columns}
                  localeText={dataGridTexts}
                  disableColumnMenu
                  disableRowSelectionOnClick
                  sx={{
                    "& .MuiDataGrid-columnHeader": {
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                    },
                  }}
                />
              </div>
            )}

            {evaluations.length === 0 && !loading && !error && (
              <Typography align="center" color="text.secondary" sx={{ mt: 4 }}>
                {companyId ? "Nenhum resultado para exibir. Selecione os filtros e gere o relatório." : "Selecione uma empresa para começar."}
              </Typography>
            )}

          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};
